import { useCallback, useEffect, useState } from "react";

import { labInsightsService } from "@/services/lab-insights.service";
import {
  LabInsightsAllGroups,
  LabInsightsGroupDetails,
  LabInsightsLanding,
  LabInsightsParameterDetails,
  LabParameterGroupId,
  PinnableParameter,
} from "@/types/lab-insights";

export function useLabInsightsLanding(memberId: string | null | undefined) {
  const [data, setData] = useState<LabInsightsLanding | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchLanding = useCallback(async () => {
    if (!memberId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await labInsightsService.getLanding(memberId);
      setData(res);
      return res;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchLanding().catch(() => {});
  }, [fetchLanding]);

  const updatePins = useCallback(
    async (canonicalParameterIds: string[]) => {
      if (!memberId) return [];
      const updated = await labInsightsService.setPins(
        memberId,
        canonicalParameterIds
      );
      setData((prev) =>
        prev ? { ...prev, pinnedParameters: updated } : prev
      );
      return updated;
    },
    [memberId]
  );

  return { data, loading, error, refetch: fetchLanding, updatePins };
}

export function useLabInsightsAllGroups(memberId: string | null | undefined) {
  const [data, setData] = useState<LabInsightsAllGroups | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetch = useCallback(async () => {
    if (!memberId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await labInsightsService.getAllGroups(memberId);
      setData(res);
      return res;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetch().catch(() => {});
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useLabInsightsGroupDetails(
  memberId: string | null | undefined,
  groupId: LabParameterGroupId | null
) {
  const [data, setData] = useState<LabInsightsGroupDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetch = useCallback(async () => {
    if (!memberId || !groupId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await labInsightsService.getGroupDetails(memberId, groupId);
      setData(res);
      return res;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memberId, groupId]);

  useEffect(() => {
    fetch().catch(() => {});
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function usePinnableParameters(
  memberId: string | null | undefined,
  open: boolean
) {
  const [parameters, setParameters] = useState<PinnableParameter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetch = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await labInsightsService.getPinnableParameters(memberId);
      setParameters(res?.parameters ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (open) fetch().catch(() => {});
  }, [open, fetch]);

  return { parameters, loading, error, refetch: fetch };
}

export function useLabInsightsParameterDetails(
  memberId: string | null | undefined,
  canonicalParameterId: string | null,
  // Raw-name fallback for unmatched OTHERS rows. When `rawName` is provided
  // (and there's no canonical id), the hook fetches Screen 3 by raw name +
  // group instead of by canonical id.
  rawName?: string | null,
  groupId?: string | null
) {
  const [data, setData] = useState<LabInsightsParameterDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetch = useCallback(async () => {
    if (!memberId) return;
    const useRawName = !canonicalParameterId && !!rawName;
    if (!canonicalParameterId && !useRawName) return;
    setLoading(true);
    setError(null);
    try {
      const res = useRawName
        ? await labInsightsService.getParameterDetailsByRawName(
            memberId,
            rawName as string,
            groupId ?? "OTHERS"
          )
        : await labInsightsService.getParameterDetails(
            memberId,
            canonicalParameterId as string
          );
      setData(res);
      return res;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memberId, canonicalParameterId, rawName, groupId]);

  useEffect(() => {
    fetch().catch(() => {});
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
